import type { ResumeSectionType } from '@applicator/shared'

export const RESUME_SECTION_SAVE_DEBOUNCE_MS = 500

export type PendingSectionSave = {
  resumeId: string | undefined
  content: Record<string, unknown>
}

export type SaveTimeoutRef = React.MutableRefObject<
  Record<ResumeSectionType, ReturnType<typeof setTimeout> | null>
>

export type PendingSectionSaveRef = React.MutableRefObject<
  Partial<Record<ResumeSectionType, PendingSectionSave>>
>

type SaveHandler = (
  sectionType: ResumeSectionType,
  content: Record<string, unknown>,
  skipStateUpdate: boolean,
  options?: { targetResumeId?: string },
) => void | Promise<unknown>

function trackPendingSave(
  pendingRef: PendingSectionSaveRef | undefined,
  sectionType: ResumeSectionType,
  resumeId: string | undefined,
  content: Record<string, unknown>,
): void {
  if (!pendingRef) return
  pendingRef.current[sectionType] = { resumeId, content }
}

function clearPendingSave(
  pendingRef: PendingSectionSaveRef | undefined,
  sectionType: ResumeSectionType,
): void {
  if (!pendingRef) return
  delete pendingRef.current[sectionType]
}

export function cancelDebouncedResumeSectionSave(
  saveTimeoutRef: SaveTimeoutRef,
  sectionType: ResumeSectionType,
): void {
  const existing = saveTimeoutRef.current[sectionType]
  if (existing) {
    clearTimeout(existing)
    saveTimeoutRef.current[sectionType] = null
  }
}

function flushPendingSection(
  saveTimeoutRef: SaveTimeoutRef,
  pendingRef: PendingSectionSaveRef | undefined,
  sectionType: ResumeSectionType,
  getActiveResumeId: () => string | undefined,
  onSave: SaveHandler,
  flushForResumeId?: string,
): void {
  cancelDebouncedResumeSectionSave(saveTimeoutRef, sectionType)
  const pending = pendingRef?.current[sectionType]
  const matchResumeId = flushForResumeId ?? getActiveResumeId()
  if (!pending?.resumeId || pending.resumeId !== matchResumeId) {
    clearPendingSave(pendingRef, sectionType)
    return
  }
  void onSave(sectionType, pending.content, true, { targetResumeId: pending.resumeId })
  clearPendingSave(pendingRef, sectionType)
}

/** Persist immediately (used for skills catalog add/delete). */
export function saveResumeSectionNow(
  saveTimeoutRef: SaveTimeoutRef,
  pendingRef: PendingSectionSaveRef | undefined,
  sectionType: ResumeSectionType,
  resumeIdAtEdit: string | undefined,
  getActiveResumeId: () => string | undefined,
  content: Record<string, unknown>,
  onSave: SaveHandler,
): void {
  trackPendingSave(pendingRef, sectionType, resumeIdAtEdit, content)
  cancelDebouncedResumeSectionSave(saveTimeoutRef, sectionType)
  if (!resumeIdAtEdit || resumeIdAtEdit !== getActiveResumeId()) {
    return
  }
  void onSave(sectionType, content, true)
  clearPendingSave(pendingRef, sectionType)
}

/**
 * Debounced persist for a resume section. Binds content to `resumeIdAtEdit` so a
 * delayed flush after navigating to another resume does not write into the wrong row.
 */
export function scheduleDebouncedResumeSectionSave(
  saveTimeoutRef: SaveTimeoutRef,
  sectionType: ResumeSectionType,
  resumeIdAtEdit: string | undefined,
  getActiveResumeId: () => string | undefined,
  content: Record<string, unknown>,
  onSave: SaveHandler,
  debounceMs = RESUME_SECTION_SAVE_DEBOUNCE_MS,
  pendingRef?: PendingSectionSaveRef,
): void {
  trackPendingSave(pendingRef, sectionType, resumeIdAtEdit, content)

  const existing = saveTimeoutRef.current[sectionType]
  if (existing) {
    clearTimeout(existing)
  }

  const capturedResumeId = resumeIdAtEdit
  saveTimeoutRef.current[sectionType] = setTimeout(() => {
    saveTimeoutRef.current[sectionType] = null
    if (!capturedResumeId || capturedResumeId !== getActiveResumeId()) {
      clearPendingSave(pendingRef, sectionType)
      return
    }
    const pending = pendingRef?.current[sectionType]
    const payload = pending?.content ?? content
    void onSave(sectionType, payload, true)
    clearPendingSave(pendingRef, sectionType)
  }, debounceMs)
}

/** Flush all pending debounced section saves (page hide / unmount). */
export function flushPendingResumeSectionSaves(
  saveTimeoutRef: SaveTimeoutRef,
  pendingRef: PendingSectionSaveRef | undefined,
  getActiveResumeId: () => string | undefined,
  onSave: SaveHandler,
  options?: { flushForResumeId?: string },
): void {
  if (!pendingRef) return
  const sectionTypes = Object.keys(pendingRef.current) as ResumeSectionType[]
  for (const sectionType of sectionTypes) {
    flushPendingSection(
      saveTimeoutRef,
      pendingRef,
      sectionType,
      getActiveResumeId,
      onSave,
      options?.flushForResumeId,
    )
  }
}
